<template>
  <div class="age">
    <p class="age__title" v-html="$t('start_v6_question')" />
    <div class="age__wrapper">
      <button
        v-for="(age, index) in ageItems"
        :key="index"
        class="age__item"
        :class="{ selected: selectedAge === index }"
        @click="selectAge(age, index)"
      >
        <div class="age__item-check">
          <div class="age__item-check-wrapper">
            <i-svg v-if="selectedAge === index" icon="icon_answer_check" />
          </div>
          <p
            class="age__text"
            :class="{ selected: selectedAge === index }"
          >
            {{ age.name }}
          </p>
        </div>
        <div class="age__item-wrapper">
          <CNuxtImg
            :src="age.image"
            :alt="age.name"
            min-height="102px"
          />
        </div>
      </button>
    </div>
  </div>
</template>

<script setup lang="ts">
import type { AgeAnswerModel } from '@/common/models/quiz.model';
import { useSelectAge } from '~/composables/selectAge.ts';
import { useQuiz } from '~/composables/quiz.ts';

const { setQuizSteps } = useQuiz();
const { select, selectedAge } = useSelectAge();
const store = useKegelStore();
const { t } = useI18n();
const localePath = useLocalePath();
const ageItems: AgeAnswerModel[] = [
  {
    id: 1,
    name: t('start_v6_answer_1'),
    value: 1,
    compatibilityScore: '87.1',
    image: '/images/age-avatars-v6/v6_img_1.webp',
  },
  {
    id: 2,
    name: t('start_v6_answer_2'),
    value: 2,
    compatibilityScore: '87.8',
    image: '/images/age-avatars-v6/v6_img_2.webp',
  },
  {
    id: 3,
    name: t('start_v6_answer_3'),
    value: 3,
    compatibilityScore: '96.7',
    image: '/images/age-avatars-v6/v6_img_3.webp',
  },
  {
    id: 4,
    name: t('start_v6_answer_4'),
    value: 4,
    compatibilityScore: '93',
    image: '/images/age-avatars-v6/v6_img_4.webp',
  },
];

const router = useRouter();
const route = useRoute();
const { $versions } = useNuxtApp();

async function selectAge(age: AgeAnswerModel, index: number): Promise<void> {
  await select(age, index);
  if ($versions.value.quiz === '3') {
    await setQuizSteps(4);
    router.push(localePath({ path: '/quiz/q2/score-rate', query: { ...route.query, user_uuid: store.userUuid } }));
  } else {
    await setQuizSteps(3);
    router.push(localePath({ path: '/quiz/0/question', query: { ...route.query, user_uuid: store.userUuid } }));
  }
}

defineExpose({ selectAge });
</script>

<style lang="scss" scoped>
.age {
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-top: 32px;

  &__title {
    text-align: center;
    font-family: $primary-fonts;
    font-size: 18px;
    font-style: normal;
    font-weight: 600;
    line-height: 24px;
    margin-bottom: 24px;
    color: $text-color;
    letter-spacing: -0.2px;
  }

  &__wrapper {
    display: flex;
    flex-direction: column;
    max-width: 375px;
    margin: 0 auto;
    gap: 8px;
    width: 100%;
  }

  &__item {
    width: 100%;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: space-between;
    border-radius: 16px;
    overflow: hidden;
    transition: all .25s ease;
    border: none;
    padding: 0;
    padding-left: 24px;
    background: $gray-bg;

    &-check {
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: 24px;

      &-wrapper {
        background: #fff;
        border-radius: 50%;
        min-width: 23px;
        min-height: 23px;

        display: flex;
        align-items: center;
        justify-content: center;

        &.checked {
          background: $text-color;
        }
      }
    }

    & svg {
      flex-shrink: 0;
    }

    &.selected {
      background: $text-color;
    }

    &-wrapper {
      min-height: 102px;
    }

    img {
      width: 100%;
      max-width: 160px;
      min-width: 160px;
      margin-left: auto;
    }
  }

  &__bottom-block {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 100%;
    background: #333E51;
    padding: 10px 0;
    gap: 12px;
  }

  &__text {
    font-family: $primary-fonts;
    font-size: 24px;
    font-style: normal;
    font-weight: 700;
    line-height: 135%; /* 32.4px */
    color: $text-color;
    transition: all .25s ease;
    white-space: nowrap;
    max-width: 100%;

    &.selected {
      color: #fff;
    }
  }
}
</style>
